import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

// Actions
import { closeViewCampaignTargetGroupForm } from "../../actions/campaign/CampaignActions";
import { getTargetGroupAttributes } from "../../actions/org/OrgTargetGroupActions";

// Constants and Utils
import { FormDataTargets } from "../../constants/GeneralConstants";
import { checkIsCustomTgPresent } from "../../utils/CampaignCustomTgUtils";

// Components
import AlertMessage from "../../mavin/components/alert-message/AlertMessage";

// Page Functions
function getSelectedTgAttributes(tgAttributesIds = [], targetGroupAttrs = {}) {
  const constructedData = {
    GENDER: [],
    AGE_GROUP: [],
    INCOME_GROUP: [],
  };

  // if no selection
  if (tgAttributesIds.length === 0) {
    return [];
  }

  // flattened array for all attributes
  const flattenedAttrs = Object.values(targetGroupAttrs).flat();

  // process only selected attributes
  flattenedAttrs.forEach((attr) => {
    if (!tgAttributesIds.includes(attr.id)) {
      return;
    }

    const { type } = attr;

    if (type === "GENDER") {
      constructedData.GENDER.push(attr);
    } else if (type === "AGE_GROUP") {
      constructedData.AGE_GROUP.push(attr);
    } else if (type === "INCOME_GROUP") {
      constructedData.INCOME_GROUP.push(attr);
    }
  });

  return [
    {
      name: "Gender",
      attributes: constructedData.GENDER,
    },
    {
      name: "Age Group",
      attributes: constructedData.AGE_GROUP,
    },
    {
      name: "Socio Economic Class (SEC)",
      attributes: constructedData.INCOME_GROUP,
    },
  ];
}

// get selected poiTypes
function getSelectedPoiTypes(poiTypeIds = [], poiTypesMap = {}) {
  if (poiTypeIds.length === 0) {
    return [];
  }

  return poiTypeIds.map((poiTypeId) => poiTypesMap[poiTypeId]).filter(Boolean);
}

export function SelectedAttributesSection({
  tgAttributesIds = [],
  targetGroupAttrs = {},
}) {
  // Construct the Selected TG data
  const selectedTgAttributes = getSelectedTgAttributes(
    tgAttributesIds,
    targetGroupAttrs
  );

  return (
    <div>
      {selectedTgAttributes.map((tgAttrGroup, i) => {
        const { name, attributes = [] } = tgAttrGroup;

        // skip the title when no attributes selected
        if (attributes.length === 0) {
          return null;
        }

        return (
          <div key={i} className="mb-4">
            <h5 className="font-weight-bold">{name}</h5>
            <ul className="row">
              {attributes.map((attr, i) => (
                <li key={i} className="col-3 pl-0">
                  {attr.name}
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

export function SelectedPoiTypesSection({ poiTypeIds = [], poiTypesMap = {} }) {
  // Construct the Selected TG data
  const selectedPoiTypes = getSelectedPoiTypes(poiTypeIds, poiTypesMap);

  // no poiTypes selected
  if (selectedPoiTypes.length === 0) {
    return null;
  }

  return (
    <div>
      <h5 className="font-weight-bold">Behavioural POI Types</h5>
      <ul className="row">
        {selectedPoiTypes.map((poiType, i) => (
          <li key={i} className="col-3 pl-0">
            {poiType.name}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Campaign TG View Form
 */
export default function CampaignTgViewForm({ selectedTgData = {} }) {
  const dispatch = useDispatch();

  // Selector
  const { targetGroupAttrs = {}, poiTypesMap = {} } = useSelector(
    (state) => state.orgTargetGroup.originalData
  );
  const isGetTgAttrLoading = useSelector(
    (state) => state.orgTargetGroup.isGetTgAttrLoading
  );

  // Selected TG Data
  const { tgAttributesIds = [], poiTypeIds = [] } = selectedTgData || {};

  // checks to call read tgAttr api
  const isCustomTgSelected = checkIsCustomTgPresent(selectedTgData);

  // Only call the API when :
  // 1. tgAtt and poiTypes are selected for campaign
  // 2. api data not present in state
  useEffect(() => {
    if (isCustomTgSelected) {
      const secAttrNeeded = true;
      if (Object.keys(targetGroupAttrs).length === 0) {
        dispatch(getTargetGroupAttributes(secAttrNeeded));
      }
    }
  }, [dispatch, Object.keys(targetGroupAttrs).length]);

  // functions
  function handleClose() {
    dispatch(closeViewCampaignTargetGroupForm());
  }

  return (
    <div
      className="modal fade"
      id={FormDataTargets.campaignTargetGroupViewForm}
      data-backdrop="static"
    >
      <div className="modal-dialog modal-lg" role="document">
        <div className="modal-content">
          {/* Form Header */}
          <div className="modal-header bg-alt px-5">
            <h4 className="modal-title">{"Target Group Information"}</h4>
          </div>

          {/* modal body */}
          <div className="modal-body px-5 min-h-">
            {/* If TG not selected for campaign */}
            {!isCustomTgSelected && (
              <AlertMessage
                className="justify-content-center p-3 mt-2"
                textMessage="No Target Group Selected."
                isLoading={false}
              />
            )}

            {/* Loading */}
            {isGetTgAttrLoading && (
              <AlertMessage
                className="justify-content-center p-3 mt-2"
                textMessage="Please Wait..."
              />
            )}

            {/* selected info */}
            {!isGetTgAttrLoading && (
              <>
                {/* Selected Tg Attributes */}
                <SelectedAttributesSection
                  tgAttributesIds={tgAttributesIds}
                  targetGroupAttrs={targetGroupAttrs}
                />

                {/* Selected Poi Types */}
                <SelectedPoiTypesSection
                  poiTypeIds={poiTypeIds}
                  poiTypesMap={poiTypesMap}
                />
              </>
            )}
          </div>

          {/* Form Footer */}
          <div className="modal-footer border-top px-5">
            <button
              type="button"
              className="btn btn-primary btn-action mt-2 rounded-lg shadow-none"
              data-dismiss="modal"
              onClick={handleClose}
            >
              {"Close"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
